import { v4 as uuidv4 } from "uuid";

import { CptInput } from "custom-models";
import { CptCode } from "custom-models";
import { CaseDiagnosis } from "custom-models";

import { CaseDetailState } from "shared/reducers/caseDetailReducer";

export const cptString = (code: CptCode) => {
  return `${code.code}${code.modifier ? `-${code.modifier}` : ""}`;
};

// Helper method for deconstructing CPT arrays sent by the API
export const reduceCptCodes = (codes: CptCode[]) => {
  if (!codes?.length) {
    return [{ code: { code: "" }, quantity: 1, id: uuidv4(), error: false }];
  }
  return [
    ...codes
      .reduce((acc: Map<string, CptInput>, cptCode: CptCode) => {
        const code = cptString(cptCode);
        if (acc.has(code)) {
          acc.get(code)!.quantity! += 1;
        } else {
          acc.set(code, {
            code: cptCode,
            quantity: 1,
            id: uuidv4(),
            error: false,
          });
        }
        return acc;
      }, new Map())
      .values(),
  ];
};

// Helper method for reconstructing CPT arrays for the payload for the API
export const createApiCptList = (codes: CptInput[]) =>
  codes.reduce((acc: CptCode[], cptCode: CptInput) => {
    return cptCode.code
      ? acc.concat(
          Array(cptCode.quantity || 0).fill({
            ...cptCode.code,
            modifier: cptCode.code.modifier,
          })
        )
      : acc;
  }, []);

// Helper method for building validation requirements
export const minimumRequirementsForDiagnosis = (d: CaseDiagnosis) => {
  return {
    diagnosis: !!d?.diagnosis?.trim(),
    microscopic: !!d?.microscopic?.trim(),
  };
};

// Helper method to format a specific diagnosis for validation
export const validityOfDiagnosis = (d: CaseDiagnosis) => {
  const requirements = minimumRequirementsForDiagnosis(d);
  return {
    specimen: d.specimen,
    missing: Object.entries(requirements)
      .filter(([_, valid]) => !valid)
      .map(([reason, _]) => reason),
    valid: Object.values(requirements).every(Boolean),
  };
};

// Helper method to determine if all slides have been viewed on a case
export const allSlidesViewedOnCase = (
  caseDetailObject: CaseDetailState,
  currentSlideId?: number
) => {
  return caseDetailObject.slides.every(
    (s) => s.id === currentSlideId || s.was_previously_viewed
  );
};

// Helper method to determine if all specimen have been diagnosed on a case
// Note that the context of `specimenDetail` is different depending on
// ... what user is looking at which case, and in what status the Case is in
// Abstracted out of `DiagnosisDialog` to be shared with SignOutButton.tsx
export const allSpecimenDiagnosedOnCase = (
  caseDetailObject: CaseDetailState
) => {
  return caseDetailObject.specimenDetail.every(
    (specimen) => specimen.diagnoses.length
  );
};
